import _ from "lodash";
import {
  DragDropInsertMethod,
  ELayoutPosition,
  ESymbolCategory,
  EUserAccessPart,
  GridSystemCount,
  IAction,
  IActionAccessDto,
  IComponentDto,
  IComponentSet,
  IComponentSetDto,
  IComponentSetMeta,
  IDisplayItemProperty,
  IPropertyDefinition,
  IScreenDesign,
  IScreenDesignDictionary,
  ISymbol,
  SpecialDIIds
} from "../index";
import { IComponent } from "../src/models/component";
import { IDisplayItem } from "../src/models/display-item";
import { IDisplayItemComponent } from "../src/models/display-item-component";
import { KeyCombinations } from "../src/models/key-combinations";
import { IMasterLayoutMeasurements } from "../src/models/master-layout-measurements";
import { IMasterSidePanelStyle } from "../src/models/master-side-panel-style";
import { IParentData } from "../src/models/parent-data";
import { IProject } from "../src/models/project";

declare let require: any;
const confJson = require("../json/conf.json");

const isCtrl = (keyCode: number) => {
  if (keyCode === 17 || keyCode === 91) return true;
  return false;
};

const getBaseUrlFromFullUrl = (fullUrl: string) => {
  const pathArray = fullUrl.split("/");
  const protocol = pathArray[0];
  const host = pathArray[2].replace(/:[0-9]*$/, "");
  const url = `${protocol}//${host}`;
  return url;
};

const getBackendUrlFromFullUrl = (fullUrl: string) => {
  const baseUrl = getBaseUrlFromFullUrl(fullUrl);
  if (baseUrl === "http://localhost") return `${baseUrl}:3772/v1/api`;
  if (baseUrl === "https://localhost") return `${baseUrl}:44300/v1/api`;
  return getApiUrlFromBaseUrl(baseUrl);
};

const getNodeUrlFromFullUrl = (fullUrl: string) => {
  const baseUrl = getBaseUrlFromFullUrl(fullUrl);
  if (baseUrl === "http://localhost") return `${baseUrl}:4000`;
  if (baseUrl === "https://localhost") return `${baseUrl}:44311/v1/api`;
  return `${baseUrl}:5000`;
};

const getApiUrlFromBaseUrl = (baseUrl: string) => {
  const re = new RegExp("(.*//)([a-zA-Z0-9-]+)(..*)");
  return baseUrl.replace(re, "$1$2api$3/v1/api");
};

const getAiChatUrlFromFullUrl = (fullUrl: string) => {
  const baseUrl = getBaseUrlFromFullUrl(fullUrl);
  if (baseUrl === "http://localhost") return `${baseUrl}:3789`;
  if (baseUrl === "https://localhost") return `${baseUrl}:3789`;
  const re = new RegExp("(.*//)([a-zA-Z0-9-]+)(..*)");
  return baseUrl.replace(re, "$1$2aichat$3");
};

declare let window: any;
export class CommonHelperFunctions {
  static GetBackendUrl() {
    // Web component case
    let backendUrl = "";
    if (
      confJson?.domainName !== undefined &&
      confJson?.applicationName !== undefined &&
      confJson?.applicationName !== "" &&
      window.kuika !== undefined &&
      window.kuika.apps !== undefined &&
      window.kuika.apps.length !== undefined &&
      window.kuika.apps.length > 0
    ) {
      window.kuika.apps.forEach((app: any) => {
        if (app.name === confJson.applicationName && app.domain === confJson.domainName) {
          backendUrl = app.backEndUrl;
        }
      });
    }
    if (backendUrl !== "") {
      return backendUrl;
    }

    // JSON case
    if (confJson?.backendUrlOverride?.length > 1) {
      return confJson.backendUrlOverride;
    }

    // iframe case
    if (window.location !== window.parent.location) {
      return getBackendUrlFromFullUrl(window.location.origin);
    }

    // Normal case :)
    return getBackendUrlFromFullUrl(window.location.href);
  }

  static getPythonBackendUrl() {
    const baseUrl = getBaseUrlFromFullUrl(window.location.href);

    if (baseUrl.includes("localhost")) {
      return `${baseUrl}:5002`;
    }

    return "https://test2-ai.kuika.com";
  }

  static GetAiChatUrl() {
    return getAiChatUrlFromFullUrl(window.location.href);
  }

  static GetNodeUrl() {
    if (confJson?.nodeUrlOverride?.length > 1) {
      return confJson.nodeUrlOverride;
    }

    // iframe case
    if (window.location !== window.parent.location) {
      return getNodeUrlFromFullUrl(window.location.origin);
    }

    return getNodeUrlFromFullUrl(window.location.href);
  }

  static getKeyCombinationsFromKeysForDesignWindows = (event: KeyboardEvent): KeyCombinations | undefined => {
    if (!event) return;
    const isCtrlOrCmd: boolean = event.ctrlKey || event.metaKey;

    if (event.code === "Backspace" || event.code === "Delete") {
      return KeyCombinations.delete;
    }

    if (isCtrlOrCmd && event.code === "KeyZ") {
      return KeyCombinations.ctrlZ;
    }

    if (isCtrlOrCmd && event.code === "KeyY") {
      return KeyCombinations.ctrlY;
    }

    if (isCtrlOrCmd && event.code === "KeyC") {
      return KeyCombinations.ctrlC;
    }

    if (isCtrlOrCmd && event.code === "KeyV") {
      return KeyCombinations.ctrlV;
    }

    if (isCtrlOrCmd && event.code === "KeyF") {
      return KeyCombinations.ctrlF;
    }

    if (isCtrlOrCmd && event.code === "KeyD") {
      return KeyCombinations.ctrlD;
    }

    if (isCtrlOrCmd && event.code === "KeyS") {
      return KeyCombinations.ctrlS;
    }

    if (isCtrlOrCmd && event.shiftKey && event.code === "KeyS") {
      return KeyCombinations.ctrlShiftS;
    }

    if (isCtrlOrCmd && event.altKey && event.code === "KeyC") {
      return KeyCombinations.ctrlAltC;
    }

    if (isCtrlOrCmd && event.altKey && event.code === "KeyT") {
      return KeyCombinations.ctrlAltT;
    }

    if (event.altKey && event.code === "Digit1") {
      return KeyCombinations.alt1;
    }

    if (event.altKey && event.code === "Digit2") {
      return KeyCombinations.alt2;
    }

    if (event.altKey && event.code === "Digit3") {
      return KeyCombinations.alt3;
    }

    if (event.altKey && event.code === "Digit4") {
      return KeyCombinations.alt4;
    }

    if (event.altKey && event.code === "Digit5") {
      return KeyCombinations.alt5;
    }

    if (event.altKey && event.code === "Digit6") {
      return KeyCombinations.alt6;
    }

    if (event.altKey && event.code === "Digit7") {
      return KeyCombinations.alt7;
    }

    if (event.altKey && event.code === "Digit8") {
      return KeyCombinations.alt8;
    }

    if (event.altKey && event.code === "Digit9") {
      return KeyCombinations.alt9;
    }

    return undefined;
  };

  static getKeyCombinationsFromKeysForNavigation = (pressedKeys: any): KeyCombinations | undefined => {
    if (pressedKeys === undefined) return;
    if (pressedKeys.length === 2) {
      // if (pressedKeys[0] === 18 && (pressedKeys[1] === 52 || pressedKeys[1] === 100)) return KeyCombinations.alt4;
      // else if (pressedKeys[0] === 18 && (pressedKeys[1] === 53 || pressedKeys[1] === 101)) return KeyCombinations.alt5;
      // else if (pressedKeys[0] === 18 && (pressedKeys[1] === 54 || pressedKeys[1] === 102)) return KeyCombinations.alt6;
      // else if (pressedKeys[0] === 18 && (pressedKeys[1] === 55 || pressedKeys[1] === 103)) return KeyCombinations.alt7;
      // else if (pressedKeys[0] === 18 && (pressedKeys[1] === 56 || pressedKeys[1] === 104)) return KeyCombinations.alt8;
    }
  };

  static isMac = () => {
    return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
  };

  static getParentDataForDraggableComponent = (
    kuikaid: string,
    dict: IScreenDesignDictionary,
    project: IProject
  ): IParentData => {
    const parentData = {} as IParentData;
    parentData.componentNamesWhichCanBeParent = [];
    // const di = CommonHelperFunctions.findDisplayItemByKuikaId(kuikaid, DIs);
    const di = dict[parseInt(kuikaid, 10)];
    if (di && project) {
      if (di.parentId && CommonHelperFunctions.isDiChildComponent(di, project)) {
        const parentDi = dict[di.parentId];

        if (parentDi) {
          const parentComp = project.componentSetObj.components.find((x) => x.id === parentDi.item?.id);
          parentData.currentParentComponentName = parentComp?.name ? parentComp.name : "";
        }
      }

      const DIComp = project.componentSetObj.components.find((x) => x.id === di.item.id);
      if (DIComp) {
        const parentComps = project.componentSetObj.components.filter((c) => {
          return c.childrenInfo && c.childrenInfo.filter((ch) => ch.name === DIComp.name).length > 0;
        });
        parentData.componentNamesWhichCanBeParent = parentComps.map((x) => x.name);
      }
    }

    return parentData;
  };

  static isDiChildComponent = (di: IDisplayItem, project: IProject): boolean => {
    const comp = project.componentSetObj.components.find((x) => x.id === di.item.id);
    if (comp) return comp.isItChildItem;
    return false;
  };

  static getAllActionsWithConfirmation = (DIs: IDisplayItem[]): IAction[] => {
    let result: IAction[] = [];
    DIs.forEach((di) => {
      result = [...result, ...this.getAllActionsByDi(di)];
      const childDIs = CommonHelperFunctions.getAllChildrenForDI(di);
      childDIs?.forEach((childDi) => {
        result = [...result, ...this.getAllActionsByDi(childDi)];
      });
    });
    return result.filter((a) => a.confirmationMessage);
  };

  static getAllNotifyActionsWithFixedValue = (DIs: IDisplayItem[]): IAction[] => {
    let result: IAction[] = [];
    DIs.forEach((di) => {
      result = [...result, ...this.getAllActionsByDi(di)];
      const childDIs = CommonHelperFunctions.getAllChildrenForDI(di);
      childDIs?.forEach((childDi) => {
        result = [...result, ...this.getAllActionsByDi(childDi)];
      });
    });
    return result.filter(
      (a) => a.label == "Notify" && a.parameters[0].symbol?.isFixed && a.parameters[0].symbol?.fixedValue.length
    );
  };

  static getAllActionsWithConfirmationAction = (DIs: IDisplayItem[]): IAction[] => {
    let result: IAction[] = [];
    DIs.forEach((di) => {
      result = [...result, ...this.getAllActionsByDi(di)];
      const childDIs = CommonHelperFunctions.getAllChildrenForDI(di);
      childDIs?.forEach((childDi) => {
        result = [...result, ...this.getAllActionsByDi(childDi)];
      });
    });
    return result.filter(
      (a) =>
        a.label === "Confirmation" &&
        a.parameters.find((p) => p.name === "confirmationMessage")?.symbol?.category === ESymbolCategory.fixed
    );
  };

  static getAllActions = (DIs: IDisplayItem[]): IAction[] => {
    let result: IAction[] = [];
    DIs.forEach((di) => {
      result = [...result, ...this.getAllActionsByDi(di)];
      const childDIs = CommonHelperFunctions.getAllChildrenForDI(di);
      childDIs?.forEach((childDi) => {
        result = [...result, ...this.getAllActionsByDi(childDi)];
      });
    });
    return result;
  };

  static getAllActionsByDi = (di: IDisplayItem): IAction[] => {
    let result: IAction[] = [];
    di?.item?.events?.forEach((e) => {
      e.actions?.forEach((a) => {
        result = [...result, ...this.getChildActionFromAction(a)];
      });
    });
    return result;
  };

  static getChildActionFromAction = (action: IAction): IAction[] => {
    let result: IAction[] = [action];
    action?.ifThenElseData?.ifActions?.forEach((ifAction) => {
      result = [...result, ...this.getChildActionFromAction(ifAction)];
    });
    action?.ifThenElseData?.elseActions?.forEach((elseAction) => {
      result = [...result, ...this.getChildActionFromAction(elseAction)];
    });
    action?.recurringActionsData?.recurringActions?.forEach((recurringAction) => {
      result = [...result, ...this.getChildActionFromAction(recurringAction)];
    });
    action?.mqttSubscribeActionData?.mqttSubscribeActions?.forEach((mqttSubscribeAction) => {
      result = [...result, ...this.getChildActionFromAction(mqttSubscribeAction)];
    });
    action?.forEachData?.actions?.forEach((forAction) => {
      result = [...result, ...this.getChildActionFromAction(forAction)];
    });
    return result;
  };

  static getAllChildrenForDisplayItems = (displayItems: IDisplayItem[]): IDisplayItem[] => {
    return displayItems.flatMap((di) => this.getAllChildrenForDI(di));
  };

  static getAllChildrenForDI = (di: IDisplayItem): IDisplayItem[] => {
    const result: IDisplayItem[] = [];

    if (!di.children?.length) return result;

    const queue: IDisplayItem[] = [...di.children];
    result.push(...di.children);

    let i: number = 0;

    while (i < queue.length) {
      const current = queue[i++];

      if (current.children?.length) {
        result.push(...current.children);
        queue.push(...current.children);
      }
    }

    return result;
  };

  static canIDropAChildItem = (
    sourceComponentName: string,
    targetComponentName: string,
    targetParentComponentName: string,
    sourceParentData: IParentData
  ): boolean => {
    if (sourceComponentName === targetComponentName) return true;
    if (sourceParentData.componentNamesWhichCanBeParent.indexOf(targetComponentName) !== -1) return true;
    if (sourceParentData.componentNamesWhichCanBeParent.indexOf(targetParentComponentName) !== -1) return true;
    return false;
  };

  static getComponentName = (di: IDisplayItem, components: IComponent[]) => {
    const targetItem: IDisplayItemComponent | undefined = di?.item;
    if (targetItem) {
      const component = CommonHelperFunctions.findComponentById(targetItem!.id, components);
      if (component) {
        return component.name;
      }
    }
    return "";
  };

  static findComponentById = (id: string, components: IComponent[]): IComponent | undefined => {
    let component = {} as IComponent | undefined;
    component = components.find((s) => s.id === id);
    if (!component) {
      const htmlComponentList: IComponent[] = require("../json/html-components.json");
      component = htmlComponentList.find((s) => s.id === id);
    }
    return component;
  };

  static toCamelCase(value: string): string {
    if (!value || (this.isLetter(value[0]) && !this.isUpperCase(value[0]))) {
      return value;
    }

    let prefix = "";
    let chars = value.split("");

    if (!this.isLetter(value[0])) {
      prefix = value[0];
      chars = value.slice(1).split("");
    }

    for (let i = 0; i < chars.length; i++) {
      if (i === 1 && !this.isUpperCase(chars[i])) {
        break;
      }
      const hasNext = i + 1 < chars.length;

      // Stop when next char is already lowercase.
      if (i > 0 && hasNext && !this.isUpperCase(chars[i + 1])) {
        // If the next char is a space, lowercase current char before exiting.
        if (chars[i + 1] === " ") {
          chars[i] = chars[i].toLowerCase();
        }
        break;
      }

      chars[i] = chars[i].toLowerCase();
    }

    return prefix + chars.join("");
  }

  // Static helper method to check if a character is a letter
  private static isLetter(char: string): boolean {
    return /^[a-zA-Z]$/.test(char);
  }

  // Static helper method to check if a character is uppercase
  private static isUpperCase(char: string): boolean {
    if (!this.isLetter(char)) {
      return false;
    }

    return char === char.toUpperCase();
  }

  static compareObjectWithField(first: any, second: any, field: string) {
    if (first[field] < second[field]) {
      return -1;
    }
    if (first[field] > second[field]) {
      return 1;
    }
    return 0;
  }

  static convertComponentSetDTOToInterface(dto: IComponentSetDto): IComponentSet {
    const compSet = {} as IComponentSet;
    if (dto.json) {
      const json: IComponentSetMeta = JSON.parse(dto.json);
      compSet.scripts = json.scripts;
      compSet.styles = json.styles;
      compSet.gridSystemCount = json.gridSystemCount ? Number(json.gridSystemCount) : GridSystemCount.grid24;
      compSet.frameUrl = json.frameUrl;
      compSet.fontNames = json.fontNames;
      compSet.language = json.language;
      compSet.themes = json.themes;
      compSet.calibrateFromTop = json.calibrateFromTop;
      compSet.calibrateFromLeft = json.calibrateFromLeft;
      compSet.categoryList = json.categoryList;
    }
    compSet.id = dto.id;
    compSet.name = dto.name;

    return compSet;
  }

  static convertComponentDTOToInterface(dtos: IComponentDto[]): IComponent[] {
    const components = [] as IComponent[];

    dtos.forEach((x, idx) => {
      let icomp = {} as IComponent;
      if (x.json) {
        const json: IComponent = JSON.parse(x.json) as IComponent;
        icomp = json;
      }
      icomp.id = x.id;
      icomp.componentSetId = x.kComponentSet_Id;
      const valueProperties = icomp.properties.filter((p) => p.value !== undefined);
      valueProperties.forEach((property: IDisplayItemProperty) => {
        if (!property.symbol) {
          property.symbol = {
            category: ESymbolCategory.fixed,
            fixedValue: property.value,
            label: property.name,
            description: property.name
          } as ISymbol;
        }
        delete property.value;
      });

      components.push(icomp);
    });

    components.forEach((ic) => {
      if (ic.childrenInfo && ic.childrenInfo.length > 0) {
        ic.childrenInfo.forEach((childName) => {
          const childComp = CommonHelperFunctions.findComponentByName(childName.name, components);
          if (childComp) {
            ic.children?.push(_.cloneDeep(childComp));
          }
        });
      }
    });
    return components;
  }

  static findComponentByName = (
    name: string,
    components: IComponent[],
    isReportDesignComponentAllowed: boolean = true
  ): IComponent | undefined => {
    if (components) {
      let component = {} as IComponent | undefined;
      component = components.find(
        (s) =>
          s.name === name &&
          s.isDeprecated === false &&
          (isReportDesignComponentAllowed ? true : !s.isReportDesignComponent)
      );
      if (!component) {
        const htmlComponentList: IComponent[] = require("../json/html-components.json");
        component = htmlComponentList.find((s) => s.name === name);
      }
      return component;
    }
  };

  /**
   * A rendered slot height only wins when the slot is actually displayed and has been laid out.
   * A hidden slot contributes 0 whatever the DOM reports, and a 0/NaN measurement means "not laid
   * out yet", where the declared value is the better guess than collapsing the centre box.
   */
  private static resolveMasterSlotHeight = (declaredHeight: number, measuredHeight?: number): number => {
    if (declaredHeight === 0) return 0;
    if (measuredHeight === undefined) return declaredHeight;
    if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) return declaredHeight;
    return measuredHeight;
  };

  static calcMasterCenterHeight = (displayItems?: IDisplayItem[], measurements?: IMasterLayoutMeasurements): string => {
    if (displayItems) {
      const declaredHeaderHeight: number = CommonHelperFunctions.getMasterHeaderHeight(displayItems);
      const declaredFooterHeight: number = CommonHelperFunctions.getMasterFooterHeight(displayItems);
      const masterHeaderHeight: number = CommonHelperFunctions.resolveMasterSlotHeight(
        declaredHeaderHeight,
        measurements?.headerHeight
      );
      const masterFooterHeight: number = CommonHelperFunctions.resolveMasterSlotHeight(
        declaredFooterHeight,
        measurements?.footerHeight
      );
      return `calc(100vh - ${masterHeaderHeight + masterFooterHeight + 6}px)`;
    }
    return `100vh`;
  };

  private static getMasterLayoutPosition = (displayItems: IDisplayItem[], diId: number): number => {
    const di = displayItems.find((x: IDisplayItem) => x.id === diId);
    const layoutPosition = di?.item?.properties?.find((p) => p.name === "layoutPosition");
    return layoutPosition?.symbol?.fixedValue !== undefined ? Number(layoutPosition.symbol.fixedValue) : 0;
  };

  /**
   * Height of the master's left/right sider. Unlike the centre box this subtracts the header and
   * footer only when they span the full width (primary), because a secondary one sits beside the
   * sider rather than above it. Shares the centre's measured-over-declared rule, so a sider cannot
   * out-grow the centre and re-introduce the overhang through the flex row they share.
   */
  static calcMasterSideHeight = (displayItems?: IDisplayItem[], measurements?: IMasterLayoutMeasurements): string => {
    if (!displayItems) {
      return `100vh`;
    }
    let totalOffset: number = 0;
    const headerPosition: number = CommonHelperFunctions.getMasterLayoutPosition(
      displayItems,
      SpecialDIIds.layoutHeaderDI
    );
    if (headerPosition === ELayoutPosition.primary) {
      totalOffset += CommonHelperFunctions.resolveMasterSlotHeight(
        CommonHelperFunctions.getMasterHeaderHeight(displayItems),
        measurements?.headerHeight
      );
    }
    const footerPosition: number = CommonHelperFunctions.getMasterLayoutPosition(
      displayItems,
      SpecialDIIds.layoutFooterDI
    );
    if (footerPosition === ELayoutPosition.primary) {
      totalOffset += CommonHelperFunctions.resolveMasterSlotHeight(
        CommonHelperFunctions.getMasterFooterHeight(displayItems),
        measurements?.footerHeight
      );
    }
    return totalOffset > 0 ? `calc(100vh - ${totalOffset}px)` : `100vh`;
  };

  /**
   * Design-canvas style of a master's left/right sider wrapper. It scrolls vertically, which makes
   * its flex `min-width` 0, so it gets an explicit `min-content` floor — otherwise a centre column
   * wider than the canvas (a secondary header full of unbreakable binding placeholders) shrinks the
   * sider to nothing and clips its menu. Not `flexShrink: 0`: that would pin a sider holding long
   * wrappable text at its one-line width and starve the centre instead.
   */
  static getMasterSidePanelStyle = (displayItems?: IDisplayItem[]): IMasterSidePanelStyle => {
    const height: string = CommonHelperFunctions.calcMasterSideHeight(displayItems);
    return { height, maxHeight: height, overflowY: "auto", minWidth: "min-content" };
  };

  static calcRootDIHeightWithMasterLayout = (
    masterDisplayItems?: IDisplayItem[],
    screenDisplayItems?: IDisplayItem[]
  ) => {
    let masterHeaderHeight = 0;
    let masterFooterHeight = 0;
    let headerHeight = 0;
    let footerHeight = 0;
    let pageHeaderHeight = 0;
    let pageFooterHeight = 0;

    if (masterDisplayItems) {
      masterHeaderHeight = CommonHelperFunctions.getMasterHeaderHeight(masterDisplayItems);
      masterFooterHeight = CommonHelperFunctions.getMasterFooterHeight(masterDisplayItems);
    }
    if (screenDisplayItems) {
      headerHeight = CommonHelperFunctions.getHeaderHeight(screenDisplayItems);
      footerHeight = CommonHelperFunctions.getFooterHeight(screenDisplayItems);
      pageHeaderHeight = CommonHelperFunctions.getPageHeaderHeight(screenDisplayItems);
      pageFooterHeight = CommonHelperFunctions.getPageFooterHeight(screenDisplayItems);
    }
    return `calc(100vh - ${masterHeaderHeight + masterFooterHeight + headerHeight + footerHeight + pageHeaderHeight + pageFooterHeight + 12}px)`;
  };

  static getMasterHeaderHeight = (displayItems: IDisplayItem[]): number => {
    let result: number = 60;
    const layoutHeaderDI = displayItems.find((x: any) => x.id === SpecialDIIds.layoutHeaderDI);
    const layoutPosition = layoutHeaderDI?.item.properties.find((p) => p.name === "layoutPosition");
    if (layoutPosition?.symbol?.fixedValue === undefined || Number(layoutPosition.symbol.fixedValue) === 0) return 0;
    const style = layoutHeaderDI?.styleObj;
    if (style?.position?.height) result = style?.position?.height;
    return result;
  };

  static getMasterFooterHeight = (displayItems: IDisplayItem[]): number => {
    let result: number = 60;
    const layoutFooterDI = displayItems.find((x: any) => x.id === SpecialDIIds.layoutFooterDI);
    const style = layoutFooterDI?.styleObj;
    const layoutPosition = layoutFooterDI?.item.properties.find((p) => p.name === "layoutPosition");
    if (layoutPosition?.symbol?.fixedValue === undefined || Number(layoutPosition.symbol.fixedValue) === 0) return 0;
    if (style?.position?.height) result = style?.position?.height;
    return result;
  };

  static getHeaderHeight = (displayItems: IDisplayItem[]): number => {
    let result: number = 0;
    const headerDI = displayItems.find((x: any) => x.id === SpecialDIIds.headerDI);
    const style = headerDI?.styleObj;
    if (style?.position?.height) result = style?.position?.height;
    if (result === 0 && headerDI) return 32;
    return result;
  };

  static getFooterHeight = (displayItems: IDisplayItem[]): number => {
    let result: number = 0;
    const footerDI = displayItems.find((x: any) => x.id === SpecialDIIds.footerDI);
    const style = footerDI?.styleObj;
    if (style?.position?.height) result = style?.position?.height;
    if (result === 0 && footerDI) return 32;
    return result;
  };

  static getPageHeaderHeight = (displayItems: IDisplayItem[]): number => {
    let result: number = 0;
    const pageHeaderDI = displayItems.find((x: any) => x.id === SpecialDIIds.pageHeaderDI);
    const style = pageHeaderDI?.styleObj;
    if (style?.position?.height) result = style?.position?.height;
    if (result === 0 && pageHeaderDI) return 32;
    return result;
  };

  static getPageFooterHeight = (displayItems: IDisplayItem[]): number => {
    let result: number = 0;
    const pageFooterDI = displayItems.find((x: any) => x.id === SpecialDIIds.pageFooterDI);
    const style = pageFooterDI?.styleObj;
    if (style?.position?.height) result = style?.position?.height;
    if (result === 0 && pageFooterDI) return 32;
    return result;
  };

  static isAbsoluteDesign = (kuikaid: number, dict: IScreenDesignDictionary): boolean => {
    if (window.kuika.isAbsoluteDesign !== true) {
      return false;
    }
    if (window.kuika.isAbsoluteDesign === true) {
      const targetDI = dict[kuikaid];
      if (targetDI) {
        const diParent = this.getParentFromDict(targetDI, dict);
        if (diParent && diParent.item) {
          if (diParent.item.name === "ReportTableColumn" || diParent.item.name === "StaticTableColumn") {
            return true;
          }
          const parentdiComp = diParent.item;
          let result;
          parentdiComp.properties.forEach((p) => {
            const propertyDef = this.getPropertyDefinition(parentdiComp, p, window.kuika.project);
            if (
              propertyDef &&
              (propertyDef.isTableOptionsProp === true || propertyDef.isGoogleMapsOptionsProp === true)
            ) {
              result = p.symbol;
            }
          });
          if (result) {
            return false;
          }
          return this.isAbsoluteDesign(diParent.id, dict);
        }
        return true;
      }
      return true;
    }
    return false;
  };

  static isTableColumnChild = (kuikaid: string, dict: IScreenDesignDictionary): boolean => {
    const targetDI = dict[parseInt(kuikaid, 10)];
    if (targetDI) {
      const diParent = this.getParent(targetDI, dict);
      if (diParent && diParent.item) {
        if (diParent.item.name === "ReportTableColumn" || diParent.item.name === "StaticTableColumn") {
          return true;
        }
        return this.isTableColumnChild(diParent.id.toString(), dict);
      }
      return false;
    }
    return false;
  };

  static getParent = (di: IDisplayItem, dict: IScreenDesignDictionary): IDisplayItem | undefined => {
    if (!di?.parentId) {
      return undefined;
    }

    return dict[di.parentId];
  };

  static getParentFromDict = (di: IDisplayItem, dict: IScreenDesignDictionary = {}): IDisplayItem | undefined => {
    if (di?.parentId) {
      const parentDi = dict[di.parentId];
      return parentDi;
    }
    return undefined;
  };

  static isPositionedContainer = (di: IDisplayItem | undefined): boolean => {
    if (!di?.item?.name) return false;
    const name = di.item.name;
    return name === "Box" || name === "Vertical Panel" || name === "StaticTable" || name === "ReportTable";
  };

  static getCumulativeAncestorOffset = (
    startDI: IDisplayItem | undefined,
    dict: IScreenDesignDictionary
  ): { cumulativeLeft: number; cumulativeTop: number } => {
    let cumulativeLeft = 0;
    let cumulativeTop = 0;
    let current = startDI;
    let iterations = 0;

    while (current && iterations < 50) {
      iterations++;
      if (current.item?.name !== "Box") {
        break;
      }
      const left = current.absolutePositionInfo?.left || current.styleObj?.position?.xAxis || 0;
      const top = current.absolutePositionInfo?.top || current.styleObj?.position?.yAxis || 0;
      cumulativeLeft += left;
      cumulativeTop += top;

      if (!current.parentId) break;
      current = dict[current.parentId];
    }

    return { cumulativeLeft, cumulativeTop };
  };

  static getPropertyDefinition = (
    diComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty,
    project: IProject
  ): IPropertyDefinition | undefined => {
    if (project.componentSetObj && project.componentSetObj.components) {
      const component: IComponent | undefined = CommonHelperFunctions.findComponentById(
        diComponent?.id,
        project.componentSetObj.components
      );
      if (component) {
        const property = component.properties.find((p) => p.name === diProperty.name);
        if (property) {
          const result: IPropertyDefinition = {
            description: property.description,
            hasOptions: property.hasOptions,
            isRequired: property.isRequired,
            isTechnical: property.isTechnical,
            // isEvent: property.isEvent,
            isVisibleInFieldPicker: property.isVisibleInFieldPicker,
            isValueProp: property.isValueProp,
            isOptionsProp: property.isOptionsProp,
            isLanguageOptionsProp: property.isLanguageOptionsProp,
            isTableOptionsProp: property.isTableOptionsProp,
            isGoogleMapsOptionsProp: property.isGoogleMapsOptionsProp,
            isKanbanBoardLaneOptionsProp: property.isKanbanBoardLaneOptionsProp,
            isKanbanBoardCardOptionsProp: property.isKanbanBoardCardOptionsProp,
            isNodeTreeNodeOptionsProp: property.isNodeTreeNodeOptionsProp,
            isChatOptionsProp: property.isChatOptionsProp,
            isTreeOptionsProp: property.isTreeOptionsProp,
            isEditabilityProp: property.isEditabilityProp,
            isVisibleProp: property.isVisibleProp,
            isAdvanced: property.isAdvanced,
            isIgnoredOnCodeOutput: property.isIgnoredOnCodeOutput,
            isIgnoredOnGeneratedApp: property.isIgnoredOnGeneratedApp,
            isSupportMultiLanguage: property.isSupportMultiLanguage,
            appSupportType: property.appSupportType,
            isDeprecated: property.isDeprecated,
            name: property.name,
            options: property.options,
            tagName: property.tagName,
            type: property.type,
            typeOptions: property.typeOptions,
            defaultValue: property.symbol?.category === ESymbolCategory.fixed ? property.symbol.fixedValue : "",
            inVisibleStyles: property.inVisibleStyles
          };
          return result;
        }
      }
    }
  };

  static getTargetOffsetY = (
    offsetY: any,
    dict: IScreenDesignDictionary,
    isMove?: boolean,
    kuikaid?: string,
    isCreate?: boolean,
    targetKuikaId?: string,
    isResize?: boolean,
    insertMethod?: DragDropInsertMethod
  ) => {
    let targetOffsetY = 0;
    let currentChild: IDisplayItem | undefined;
    let targetDi: IDisplayItem | undefined;
    const rootDI = dict[SpecialDIIds.rootDI];
    if (targetKuikaId?.includes("-lastChildItem")) {
      targetKuikaId = targetKuikaId.replace("-lastChildItem", "");
    }
    if (targetKuikaId) {
      targetDi = dict[parseInt(targetKuikaId, 10)];
    }

    const targetPanel = rootDI?.children.find((x) => x.id.toString() === targetKuikaId);
    const targetPanelEl = document.getElementById(`kuika_${targetKuikaId}-lastChildItem`);
    const scrollFrame = document.getElementsByClassName(`kuika_rootDIWrapper`)[0];

    if (kuikaid) {
      if (isResize === true) {
        const di = dict[parseInt(kuikaid, 10)];
        targetKuikaId = di?.parentId?.toString();
      }
      currentChild = dict[parseInt(kuikaid, 10)];
      const currentParent: IDisplayItem | undefined = CommonHelperFunctions.getParent(currentChild, dict);

      const isInHeader = targetDi?.item?.name === "Header";
      const isInFooter = targetDi?.item?.name === "Footer";
      const isInPageHeader = targetDi?.id === SpecialDIIds.pageHeaderDI;
      const isInPageFooter = targetDi?.id === SpecialDIIds.pageFooterDI;
      const isInBox = targetDi?.item?.name === "Box";

      if (isInHeader || isInPageHeader) {
        return offsetY;
      }
      if (isInPageFooter) {
        const canvasEl = document.getElementById("kuika_rootDIWrapper");
        const rootElement = document.getElementById(`kuika_${SpecialDIIds.rootDI.toString()}-lastChildItem`);
        const canvasHeight =
          (canvasEl && canvasEl.clientHeight) ||
          (typeof window !== "undefined" && window.innerHeight) ||
          (document && (document.documentElement as any)?.clientHeight) ||
          (rootElement ? rootElement.offsetHeight : 0) ||
          0;
        const relativeMouseY = offsetY;
        const footerHeight = dict[SpecialDIIds.footerDI]?.styleObj?.position?.height || 0;
        const pageFooterHeight = dict[SpecialDIIds.pageFooterDI]?.styleObj?.position?.height || 0;
        targetOffsetY = relativeMouseY - (canvasHeight - footerHeight - pageFooterHeight);
        if (targetOffsetY < 0) {
          targetOffsetY = 0;
        }
        return targetOffsetY;
      }
      if (isInFooter) {
        const canvasEl = document.getElementById("kuika_rootDIWrapper");
        const rootElement = document.getElementById(`kuika_${SpecialDIIds.rootDI.toString()}-lastChildItem`);
        const canvasHeight =
          (canvasEl && canvasEl.clientHeight) ||
          (typeof window !== "undefined" && window.innerHeight) ||
          (document && (document.documentElement as any)?.clientHeight) ||
          (rootElement ? rootElement.offsetHeight : 0) ||
          0;
        const relativeMouseY = offsetY;
        const footerHeight = dict[SpecialDIIds.footerDI]?.styleObj?.position?.height || 0;
        targetOffsetY = relativeMouseY - (canvasHeight - footerHeight);
        if (targetOffsetY < 0) {
          targetOffsetY = 0;
        }
        return targetOffsetY;
      }
      if (isInBox) {
        const ancestorDI = currentParent && currentParent.item?.name === "Box" ? currentParent : targetDi;
        const { cumulativeTop } = CommonHelperFunctions.getCumulativeAncestorOffset(ancestorDI, dict);
        targetOffsetY = offsetY - (cumulativeTop || 0);
        if (scrollFrame && scrollFrame.scrollTop > 0) {
          targetOffsetY += scrollFrame?.scrollTop;
        }

        if (targetOffsetY < 0) {
          targetOffsetY = 0;
        }
        return targetOffsetY;
      }

      if (rootDI) {
        let adjustedOffsetY = offsetY;

        let sum = 0;
        let itemIndex = rootDI.children.findIndex((di) => di.id === targetPanel?.id) + 1;
        if (!targetPanel) itemIndex = rootDI.children.length;
        const documentHasPanel = Object.values(dict).some((di) => di.item?.name === "Panel");
        if (documentHasPanel) {
          for (let i = 0; i < itemIndex; i++) {
            const child = document.getElementById(`kuika_${rootDI.children[i].id}-lastChildItem`);
            const tableChild = document.getElementById(`kuika_${rootDI.children[i].id}`);
            if (child) {
              sum += child.offsetHeight;
            } else if (tableChild) {
              sum += tableChild.offsetHeight;
            }
          }

          if (sum) targetOffsetY = adjustedOffsetY - sum;
          if (itemIndex === 0) targetOffsetY = adjustedOffsetY;
          if (isCreate && targetPanel && targetPanelEl) targetOffsetY += targetPanelEl.offsetHeight;

          if (isMove && targetPanelEl && targetOffsetY !== undefined && targetOffsetY !== null) {
            if (targetPanelEl && targetOffsetY < 0) {
              targetOffsetY += targetPanelEl.offsetHeight;
            }
          }
        }

        if (!documentHasPanel) {
          targetOffsetY = adjustedOffsetY - sum;
        }
        if (itemIndex === 0) targetOffsetY = adjustedOffsetY;

        if (scrollFrame && scrollFrame.scrollTop > 0) {
          targetOffsetY += scrollFrame?.scrollTop;
        }

        if (currentChild?.item?.name === "Box" && isResize) {
          targetOffsetY = adjustedOffsetY;
        }
      }
    }
    if (targetOffsetY < 0) {
      targetOffsetY = 0;
    }
    return targetOffsetY;
  };

  static findChildrenThatHasHighestTop = (di: IDisplayItem | undefined, sd: IScreenDesign): number => {
    if (!di) return 0;
    let highestTop = 0;
    di.children?.forEach((child) => {
      const height = child.fixedPositionInfo?.height ?? 0;
      if (child.fixedPositionInfo?.top && child.fixedPositionInfo.top + height > highestTop) {
        highestTop = child.fixedPositionInfo.top;
        if (child?.fixedPositionInfo?.height) {
          highestTop += child.fixedPositionInfo.height;
        }
      }
    });
    return highestTop;
  };

  static findPanelDIByPosition(sd: IScreenDesign, y?: number) {
    const rootDiIdx = this.getRootDiIdxInDisplayItems(sd.displayItems);
    let totalHeight = 0;
    if (sd.displayItems) {
      for (let i = 0; i < sd.displayItems.length; i++) {
        const di = sd.displayItems[i];
        if (
          di.item &&
          di.item.name === "Header" &&
          di.styleObj &&
          di.styleObj.position &&
          di.styleObj.position.height
        ) {
          totalHeight += di.styleObj.position.height;
        }
      }
    }
    if (y === undefined) return;
    if (rootDiIdx >= 0) {
      const rootDi = sd.displayItems[rootDiIdx];

      if (rootDi.children) {
        for (let i = 0; i < rootDi.children.length; i++) {
          const di = rootDi.children[i];

          if (
            di.item &&
            (di.item.name === "Panel" ||
              di.item.name === "Table" ||
              di.item.name === "ReportTable" ||
              di.item.name === "StaticTable") &&
            di.styleObj &&
            di.styleObj.position &&
            di.styleObj.position.height
          ) {
            const diHeight = di.styleObj.position.height;

            if (totalHeight <= y && y < totalHeight + diHeight) {
              return di;
            }

            totalHeight += diHeight;
          }
        }
      }
    }

    return undefined;
  }

  static findLastDI(sd: IScreenDesign) {
    const rootDiIdx = this.getRootDiIdxInDisplayItems(sd.displayItems);
    if (rootDiIdx >= 0) {
      const rootDi = sd.displayItems[rootDiIdx];
      if (rootDi.children) {
        for (let i = rootDi.children.length - 1; i >= 0; i--) {
          const di = rootDi.children[i];
          if (
            di.item &&
            (di.item.name === "Panel" ||
              di.item.name === "Table" ||
              di.item.name === "ReportTable" ||
              di.item.name === "StaticTable")
          ) {
            return di;
          }
        }
      }
    }
  }

  static isTargetDIFooterByPosition(sd: IScreenDesign, y?: number) {
    let document = window.document;
    const iframe: any = document.getElementById("kuika_iframeAgent_canvas");
    if (iframe) {
      document = iframe.contentDocument;
    }
    const canvasHeight = document.getElementById("kuika_fullDiv")?.clientHeight;
    const footerHeight = sd.displayItems.find((di) => di.id === SpecialDIIds.footerDI)?.styleObj?.position?.height;

    if (canvasHeight && footerHeight && y && y > canvasHeight - footerHeight) {
      return true;
    }
    return false;
  }

  static isTargetDIHeaderByPosition(sd: IScreenDesign, y?: number) {
    let document = window.document;
    const iframe: any = document.getElementById("kuika_iframeAgent_canvas");
    if (iframe) {
      document = iframe.contentDocument;
    }
    const headerHeight = sd.displayItems.find((di) => di.id === SpecialDIIds.headerDI)?.styleObj?.position?.height;

    if (headerHeight && y && y < headerHeight) {
      return true;
    }
    return false;
  }

  static getRootDiIdxInDisplayItems(displayItems: IDisplayItem[]): number {
    const rootDI = displayItems.find((x) => x.id === SpecialDIIds.rootDI);
    let rootDiIdx = -1;
    if (rootDI) {
      rootDiIdx = displayItems.indexOf(rootDI);
    }

    return rootDiIdx;
  }

  /**
   * Checkes whether page is redirected by specific URL by ignoring queries
   *
   * **CAUTION: Works only for VANILLA JS redirections**
   * @param page page pathname
   * @returns {boolean}
   */
  static isRedirectedBy(page: string): boolean {
    try {
      const { origin: prevOrigin, pathname: prevPathname } = new URL(document.referrer);
      const prevUrl = prevOrigin + prevPathname;
      const baseUrl = window.location.origin;

      return prevUrl === `${baseUrl}/${page}` || prevUrl === `${baseUrl}/${page}.html`;
    } catch (_error) {
      return false;
    }
  }

  static getTableColumnEditModeRenderer = (valueType: string): string[] => {
    if (valueType === "Unknown" || valueType === "String" || valueType == "Guid") {
      return ["TextInput", "Email", "TextArea", "Password", "Url", "SelectBox", "DynamicInput"];
    }
    if (
      valueType == "Decimal" ||
      valueType == "Double" ||
      valueType == "Int16" ||
      valueType == "Int32" ||
      valueType == "Int64"
    ) {
      return ["NumberInput", "Currency", "Percent", "SelectBox", "DynamicInput"];
    }
    if (valueType == "Boolean") {
      return ["Switch", "Checkbox", "DynamicInput"];
    }
    if (valueType == "DateTime" || valueType == "Date") {
      return ["Date", "DateTime", "MonthPicker", "YearPicker", "DynamicInput"];
    }
    if (valueType == "DateTimeOffset" || valueType == "DateTime" || valueType == "Date") {
      return ["DateTime", "Date", "MonthPicker", "YearPicker"];
    }
    return ["TextInput", "SelectBox", "DynamicInput"];
  };

  static getIsTypeSimpleKuikaPrimitive = (type: string | null, includeNull: boolean = true): boolean => {
    if (includeNull && !type) {
      // Void
      return true;
    }

    const _type = type?.toLowerCase();

    return (
      _type === "string" ||
      _type === "boolean" ||
      _type === "double" ||
      _type === "decimal" ||
      _type === "int64" ||
      _type === "int32" ||
      _type === "int16" ||
      _type === "datetimeoffset" ||
      _type === "datetime" ||
      _type === "date" ||
      _type === "guid" ||
      _type === "bytea" ||
      _type === "binary"
    );
  };

  static getIsEditableTableColumnChild = (kuikaId: string, dict: IScreenDesignDictionary): boolean => {
    const activeKuikaComponentParentId = dict[parseInt(kuikaId, 10)]?.parentId;
    if (activeKuikaComponentParentId) {
      const activeKuikaComponentParent = dict[activeKuikaComponentParentId];
      return (
        activeKuikaComponentParent?.item.name === "EditableTableColumn" ||
        activeKuikaComponentParent?.item.name === "TableDefaultColumn"
      );
    }
    return false;
  };

  static getIsEditableTableColumnChildFromDict = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    const activeKuikaComponentParent = dict[kuikaId];
    if (activeKuikaComponentParent) {
      return (
        activeKuikaComponentParent?.item.name === "EditableTableColumn" ||
        activeKuikaComponentParent?.item.name === "TableDefaultColumn"
      );
    }
    return false;
  };

  static getIsTargetDiChildOfSourceDi = (
    sourceKuikaId: string,
    targetKuikaId: string,
    dict: IScreenDesignDictionary
  ): boolean => {
    const sourceDi = dict[parseInt(sourceKuikaId, 10)];
    const targetDi = dict[parseInt(targetKuikaId, 10)];

    if (!sourceDi?.children || !targetDi) return false;

    const queue: IDisplayItem[] = [...sourceDi.children];

    while (queue.length > 0) {
      const currentDi = queue.shift()!;

      if (currentDi.id === targetDi.id) {
        return true;
      }

      if (currentDi.children?.length) {
        queue.push(...currentDi.children);
      }
    }

    return false;
  };

  static getIsInteractionDisabled = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    const di = dict[kuikaId];

    if (!di) {
      return false;
    }

    if (di.isInteractionDisabled) {
      return true;
    }

    return false;
  };

  static getIsInteractionDisabledDict = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    const di: IDisplayItem | undefined = dict[kuikaId];

    if (!di) {
      return false;
    }

    if (di.isInteractionDisabled) {
      return true;
    }

    return false;
  };

  static getIsParentsChildrenInteractionDisabled = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    if (!dict[kuikaId]) return false;

    let currentDi: IDisplayItem = dict[kuikaId];

    while (currentDi.parentId) {
      const parentDi = dict[currentDi.parentId];
      if (!parentDi) return false;

      if (parentDi.isChildrenInteractionDisabled) {
        return true;
      }

      currentDi = parentDi;
    }

    return false;
  };

  static getAnyChildsInteractionDisabled = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    const di = dict[kuikaId];
    if (!di?.children?.length) return false;

    const queue: IDisplayItem[] = [...di.children];

    while (queue.length > 0) {
      const child = queue.shift()!;

      if (child.isInteractionDisabled) {
        return true;
      }

      if (child.children?.length) {
        queue.push(...child.children);
      }
    }

    return false;
  };

  static getIsChildOfDataRepeater = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    if (!dict[kuikaId]) return false;

    let currentDi: IDisplayItem = dict[kuikaId];

    while (currentDi) {
      if (
        currentDi.item?.name === "TableColumn" ||
        currentDi.item?.name === "ReportTableColumn" ||
        currentDi.item?.name === "EditableTableColumn" ||
        currentDi.item?.name === "TableTemplateColumn" ||
        currentDi.item?.name === "TableDefaultColumn" ||
        currentDi.item?.name === "StaticTableColumn"
      ) {
        return true;
      }

      if (!currentDi.parentId || currentDi.id === SpecialDIIds.rootDI || currentDi.parentId === SpecialDIIds.rootDI) {
        return false;
      }

      currentDi = dict[currentDi.parentId];
    }

    return false;
  };

  static getIsChildOfForm = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    if (!dict[kuikaId]) return false;

    let currentDi: IDisplayItem = dict[kuikaId];

    if (currentDi.parentId) {
      currentDi = dict[currentDi.parentId];
    }

    while (currentDi) {
      if (currentDi.item?.name === "Form") {
        return true;
      }

      if (!currentDi.parentId || currentDi.id === SpecialDIIds.rootDI || currentDi.parentId === SpecialDIIds.rootDI) {
        return false;
      }

      currentDi = dict[currentDi.parentId];
    }

    return false;
  };

  static getIsReportHeaderOrFooter = (kuikaId: number, dict: IScreenDesignDictionary): boolean => {
    const di = dict[kuikaId];

    if (!di) {
      return false;
    }

    if (
      (di?.item?.name === "Header" ||
        di?.item?.name === "Footer" ||
        di?.item?.name === "Page Header" ||
        di?.item?.name === "Page Footer") &&
      window.kuika?.isAbsoluteDesign === true
    ) {
      return true;
    }
    return false;
  };

  static hasUserAccess = (requiredRole: EUserAccessPart): boolean => {
    const userRoles = window?.userInfo?.accessRoles;
    if (userRoles && requiredRole) {
      return userRoles.some((role: IActionAccessDto) => requiredRole.includes(role.code));
    }
    return false;
  };

  static createScreenDesignDict = (screenDesign: IScreenDesign): IScreenDesignDictionary => {
    if (!screenDesign?.displayItems?.length) return {};

    const dict: IScreenDesignDictionary = {};
    const queue: IDisplayItem[] = [...screenDesign.displayItems];

    while (queue.length > 0) {
      const di = queue.shift()!;
      dict[di.id] = di;

      if (di.children?.length) {
        queue.push(...di.children);
      }
    }

    return dict;
  };

  static ignoreKnownConsoleWarns(): void {
    if (!window.location.href.includes("localhost")) {
      const ignoredWarnings: string[] = [
        "[webpack-dev-server",
        "Google Maps JavaScript",
        "allow-scripts and allow-same-origin",
        "It looks like there are several",
        "styled-components",
        "link preload but not used within a few second",
        "react-fast-compare cannot handle circular refs",
        "componentWillReceiveProps has been renamed"
      ];
      const originalConsoleWarns = console.warn;
      console.warn = function (...args) {
        if (args[0] && ignoredWarnings.some((element) => args[0].toString().includes(element))) {
          return;
        }
        originalConsoleWarns.apply(console, args);
      };
    }
  }

  static ignoreKnownConsoleErrors(): void {
    if (!window.location.href.includes("localhost")) {
      const ignoredErrors: string[] = [
        "is deprecated in StrictMode",
        "Invalid DOM property",
        "X-Frame-Options may only be",
        "spell it as lowercase",
        "A valid state object (or null) must be returned",
        "Each child in a list should have a unique",
        "or a non-boolean attribute",
        "Invalid value for prop",
        "is not connected to any Form element.",
        "onResize is not a function",
        "listener to be a function",
        "Unknown event handler property",
        "validateDOMNesting(...)",
        "Failed prop type: Invalid prop"
      ];
      const originalConsoleErrors = console.error;
      console.error = function (...args) {
        if (args[0] && ignoredErrors.some((element) => args[0].toString().includes(element))) {
          return;
        }
        originalConsoleErrors.apply(console, args);
      };
    }
  }

  static getClientId(): string {
    let appKey = "default";
    try {
      const backEndUrl = CommonHelperFunctions.GetBackendUrl() || "";
      const frontEndUrl = window.location.origin || "";
      appKey = `${frontEndUrl}|${backEndUrl}`.toLowerCase();
    } catch (_) {
      // ignore
    }

    const STORAGE_KEY = `kuika_client_id${appKey}`;

    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (existing && existing.trim().length > 0) return existing;

      const newId = CommonHelperFunctions.generateUuidV4();
      localStorage.setItem(STORAGE_KEY, newId);
      return newId;
    } catch (_) {
      return CommonHelperFunctions.generateUuidV4();
    }
  }

  private static generateUuidV4(): string {
    const c: any = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();

    const bytes = new Uint8Array(16);
    if (c?.getRandomValues) c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}
